import {
  createContext, useContext, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { FiltersQueryParamsStates, JobSearchQueryParams, TalentSearchQueryParams } from '@type/filters';


// Interface for the search context value (queryParams + setter)
interface HeaderSearchContextType extends FiltersQueryParamsStates<TalentSearchQueryParams | JobSearchQueryParams> {
  inputPlaceholder?: string;
}

// Interface for the provider's internal context (value + updater)
interface HeaderSearchContextInternal {
  value: HeaderSearchContextType | undefined;
  setHeaderSearchContext: (ctx: HeaderSearchContextType | undefined) => void;
}


/**
 * @description The context for the header search. It contains the query params and a state setter to update them.
 * * **queryParams** - the query params (ie: for the filters). Default is undefined.
 * * **setQueryParams** - the function to set the query params. Default is undefined.
 * * **inputPlaceholder** - the placeholder for the search input. Default is empty string.
 *
 * With this, you won't need to use route conditionals to see if you're in the right Page, you will only need to check
 * if the *queryParams* or *setQueryParams* params are not undefined.
 *
 * @example
 * Page file, the place where you're declaring the context value.
 * // **************************************
 * import { useSetHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
 *
 * // Inside the Page component
 * const MyPage = (props: MyPageProps) => {
 *   ...
 *
 *   // Example of getting the query params and the function to set them from useFiltersQueryParams hook
 *   const {
 *     queryParams,
 *     setQueryParams,
 *     setCurrentPage,
 *   } = useFiltersQueryParams({
 *     serverQueryParams: objFilterParams,
 *     initialPage: page,
 *     keepAsCommaSeparatedValues: ['spokenLanguages'],
 *   });
 *
 *   ...
 *
 *   // Push the context value up to the provider. The provider lives above the layout,
 *   // so the header components (FilterButton, SearchJobSection) can consume it.
 *   useSetHeaderSearchContext({ queryParams, setQueryParams });
 *
 *   return (...);
 * };
 *
 *
 * // Search input component file
 * // **************************************
 * export const SearchSection = () => {
 *   ...
 *   // Get the query params from the context
 *   const { queryParams, setQueryParams } = useContext(HeaderSearchContext);
 *
 *   if (!queryParams || !setQueryParams) {
 *     // you're not in the MyPage page
 *   };
 * };
 */

// The context consumed by header components (FilterButton, SearchJobSection, etc.)
export const HeaderSearchContext = createContext<HeaderSearchContextType | undefined>(
  { queryParams: {}, setQueryParams: () => {}, inputPlaceholder: '' },
);

// Internal context for the setter (used by page components to push their values)
const HeaderSearchSetterContext = createContext<HeaderSearchContextInternal['setHeaderSearchContext']>(() => {});


/**
 * @description Provider for the HeaderSearchContext. Place this above the layout in _app.tsx.
 * Page components use `useSetHeaderSearchContext` to push their queryParams/setQueryParams up,
 * and header components consume `HeaderSearchContext` as before.
 */
export const HeaderSearchContextProvider = ({ children }: { children: ReactNode }) => {
  const [searchContext, setSearchContext] = useState<HeaderSearchContextType | undefined>(undefined);

  const setHeaderSearchContext = useCallback((ctx: HeaderSearchContextType | undefined) => {
    setSearchContext((prev) => {
      // Shallow compare to avoid unnecessary re-renders
      if (
        prev?.queryParams === ctx?.queryParams
        && prev?.setQueryParams === ctx?.setQueryParams
        && prev?.inputPlaceholder === ctx?.inputPlaceholder
      ) {
        return prev; // Same reference → no re-render
      }
      return ctx;
    });
  }, []);

  return (
    <HeaderSearchSetterContext.Provider value={setHeaderSearchContext}>
      <HeaderSearchContext.Provider value={searchContext}>
        {children}
      </HeaderSearchContext.Provider>
    </HeaderSearchSetterContext.Provider>
  );
};


/**
 * @description Hook for page components to push their search context (queryParams, setQueryParams)
 * up to the HeaderSearchContext provider. Automatically cleans up on unmount.
 *
 * @param context - The search context value to provide, or undefined to clear.
 *
 * @example
 * // Inside a page component:
 * useSetHeaderSearchContext({ queryParams, setQueryParams });
 */
export const useSetHeaderSearchContext = (context: HeaderSearchContextType | undefined) => {
  const setHeaderSearchContext = useContext(HeaderSearchSetterContext);

  // Use individual properties as dependencies instead of the context object,
  // because { queryParams, setQueryParams } creates a new object on every render.
  const queryParams = context?.queryParams;
  const setQueryParamsFn = context?.setQueryParams;
  const inputPlaceholder = context?.inputPlaceholder;

  useEffect(() => {
    let value: HeaderSearchContextType | undefined;

    if (queryParams !== undefined || setQueryParamsFn !== undefined) {
      value = {
        queryParams: queryParams ?? {},
        setQueryParams: setQueryParamsFn ?? (() => {}),
        inputPlaceholder,
      };
    }

    setHeaderSearchContext(value);
  }, [queryParams, setQueryParamsFn, inputPlaceholder, setHeaderSearchContext]);

  // Cleanup on unmount — reset context when leaving the page
  useEffect(() => () => {
    setHeaderSearchContext(undefined);
  }, [setHeaderSearchContext]);
};

/**
 * @description Hook that returns a function to immediately clear the HeaderSearchContext.
 * Use it when you need to imperatively reset the header search (e.g. on context switch).
 */
export const useClearHeaderSearchContext = () => {
  const setHeaderSearchContext = useContext(HeaderSearchSetterContext);
  return useCallback(() => setHeaderSearchContext(undefined), [setHeaderSearchContext]);
};
